import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { PublicLinkView } from "@shared/modules/x-avocats";
import { ApiError } from "../../../core/lib/api";
import { BrandMark } from "../../../core/components/ui/brand-mark";
import { Button } from "../../../core/components/ui/button";
import { Field, Input } from "../../../core/components/ui/input";
import { blueprint } from "../../../core/blueprint";
import { deposit, publicFileUrl, publicView, requestCode, verifyCode } from "../lib/api";

const STATE_TEXT: Record<PublicLinkView["state"], string> = {
  valide: "",
  expire: "Ce lien a expiré. Demandez-en un nouveau à votre interlocuteur.",
  revoque: "Ce lien a été révoqué.",
  consomme: "Ce lien a déjà été utilisé : les documents ont été récupérés (ou déposés).",
};

/** Page publique, hors connexion : code par courriel puis récupération ou dépôt. */
export function PublicLinkPage() {
  const { token = "" } = useParams();
  const [view, setView] = useState<PublicLinkView | null>(null);
  const [missing, setMissing] = useState(false);
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [deposited, setDeposited] = useState<string[]>([]);

  useEffect(() => {
    publicView(token)
      .then(setView)
      .catch(() => setMissing(true));
  }, [token]);

  const askCode = async () => {
    setBusy(true);
    setError(null);
    try {
      await requestCode(token);
      setSent(true);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 429 ? "Un code vient d'être envoyé : patientez une minute." : "Envoi impossible");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      setView(await verifyCode(token, code.trim()));
    } catch (e) {
      const c = e instanceof ApiError ? e.code : "";
      setError(c === "locked" ? "Trop d'essais : demandez un nouveau code." : c === "code_expired" ? "Code expiré : demandez-en un nouveau." : "Code refusé");
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await deposit(token, files);
      setDeposited([...deposited, ...r.accepted]);
      if (r.rejected.length) setError(`Refusé : ${r.rejected.map((x) => x.name).join(", ")} (format non accepté ou lien épuisé)`);
      setFiles([]);
      setView(await publicView(token));
    } catch {
      setError("Dépôt impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <div className="login__form login__form--wide lien">
        <div className="login__head">
          <BrandMark size={40} />
          <div>
            <h1 className="login__title">{view?.organisationName ?? blueprint.name}</h1>
            <p className="login__subtitle">Échange sécurisé de documents</p>
          </div>
        </div>

        {missing ? <p className="login__error">Ce lien n'existe pas.</p> : null}
        {view && view.state !== "valide" ? <p className="login__error">{STATE_TEXT[view.state]}</p> : null}

        {view && view.state === "valide" && !view.verified ? (
          <>
            {view.message ? <p className="lien__message">{view.message}</p> : null}
            <p className="login__hint">Pour protéger ces documents, un code de vérification est envoyé à l'adresse courriel du destinataire de ce lien.</p>
            {!sent ? (
              <Button variant="primary" disabled={busy} onClick={askCode}>
                Recevoir le code par courriel
              </Button>
            ) : (
              <>
                <Field label="Code reçu par courriel">
                  <Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" autoFocus />
                </Field>
                <Button variant="primary" disabled={busy || code.trim().length !== 6} onClick={verify}>
                  Valider
                </Button>
                <Button variant="ghost" disabled={busy} onClick={askCode}>
                  Renvoyer un code
                </Button>
              </>
            )}
          </>
        ) : null}

        {view && view.state === "valide" && view.verified && view.direction === "telechargement" ? (
          <>
            <p className="login__hint">Chaque pièce peut être téléchargée un nombre limité de fois ; le lien expire ensuite.</p>
            <ul className="lien__docs">
              {(view.documents ?? []).map((d) => (
                <li key={d.id}>
                  <span>
                    {d.originalName} <span className="muted">· {d.label}</span>
                  </span>
                  {d.remainingUses > 0 ? (
                    <a className="btn btn--primary btn--sm" href={publicFileUrl(token, d.id)} onClick={() => setTimeout(() => publicView(token).then(setView).catch(() => undefined), 1500)}>
                      Télécharger
                    </a>
                  ) : (
                    <span className="muted">récupéré</span>
                  )}
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {view && view.state === "valide" && view.verified && view.direction === "depot" ? (
          <>
            <p className="login__hint">Déposez vos documents (PDF, Word ou images). Ils rejoignent directement le dossier.</p>
            <Field label="Fichiers">
              <Input type="file" multiple accept=".pdf,.docx,image/*" onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
            </Field>
            <Button variant="primary" disabled={busy || files.length === 0} onClick={send}>
              Déposer {files.length ? `(${files.length})` : ""}
            </Button>
            {deposited.length ? <p className="mfa__ok">Déposé : {deposited.join(", ")}</p> : null}
          </>
        ) : null}

        {error ? (
          <p className="login__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
